import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useFetchServicePreferences } from '@/hooksApi';
import { cn } from '@/lib/utils';
import type { UnavailablePreference } from '@/types';

/**
 * Owner ruling 2026-08-25 (BOXY-214 / BOXY-215):
 *
 *   "If I choose Save and my order ships to Karbala and there is no Save
 *    service for Karbala, we should show the second step — and mention it:
 *    your Save predefined doesn't exist for this province, so you should
 *    choose."
 *
 * Falling back was ALREADY the behaviour. It was silent, and that silence is
 * what made the feature feel broken: the merchant set a preference, got
 * something else, and was told nothing.
 *
 * ⚠️ THIS RENDERS IN TWO PLACES, and the second one is not obvious. When the
 * merchant's PROVINCE RULE fails but their ACCOUNT DEFAULT resolves, the step
 * is skipped — `source` is a decided one — and yet a preference of theirs
 * silently did nothing. So the notice belongs on the Select Service step AND
 * on the summary; it is additive information, never "why you are on this
 * page". Deriving one from the other is the SVC-F2 shape of defect.
 */
interface Props {
  unavailable: UnavailablePreference | null;
  /** BOXY-588 — a refusal sentence (e.g. the backend's) shown instead of the computed one. */
  refusal?: string;
  className?: string;
}

const UnavailablePreferenceNotice = ({ unavailable, refusal, className }: Props) => {
  const { t } = useTranslation();
  const { data: preferences } = useFetchServicePreferences();

  const box = (text: string) => (
    <div
      role='status'
      data-testid='unavailable-preference-notice'
      className={cn(
        'flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3',
        'dark:border-amber-900/40 dark:bg-amber-950/30',
        className,
      )}
    >
      <AlertTriangle
        className='mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-500'
        strokeWidth={1.5}
      />
      <p className='text-sm leading-relaxed text-amber-900 dark:text-amber-200'>{text}</p>
    </div>
  );

  if (!unavailable) {
    return refusal ? box(refusal) : null;
  }

  // Show the name the merchant chose in Settings, not the code they never saw.
  // The code is the fallback, not the target: a retired service may be gone
  // from the catalogue entirely, which is exactly when this notice fires.
  const serviceName =
    preferences?.services?.find((item) => item.code === unavailable.code)?.title ??
    unavailable.code;

  // The destination, named — `province_code` is canonical ('BG') while the
  // regions API serves legacy ('BGD'), so it can never be looked up. The
  // backend resolves the name itself and sends it localized.
  const where = unavailable.province_name ?? null;

  // 🔴 EVERY reason needs its OWN sentence. `not_capable` was added on the wire
  // before this branch existed and fell into the not_offered copy, telling a
  // merchant their service "is not available for this destination" when the
  // truth is that it cannot carry a temperature-controlled order — a wrong
  // sentence sends them to change the wrong thing.
  const message = (() => {
    switch (unavailable.reason) {
      case 'retired':
        return String(
          t('service_preference_retired', {
            service: serviceName,
            defaultValue: '{{service}} is no longer available. Please choose a service.',
          }),
        );
      case 'not_capable':
        return String(
          t('service_preference_not_capable', {
            service: serviceName,
            defaultValue:
              '{{service}} cannot deliver temperature-controlled orders. Please choose a service for this order.',
          }),
        );
      default:
        return where
          ? String(
              t('service_preference_not_offered_province', {
                service: serviceName,
                province: where,
                defaultValue:
                  '{{service}} is not available for {{province}}. Please choose a service.',
              }),
            )
          : String(
              t('service_preference_not_offered', {
                service: serviceName,
                defaultValue:
                  '{{service}} is not available for this destination. Please choose a service.',
              }),
            );
    }
  })();

  return box(refusal ?? message);
};

export default UnavailablePreferenceNotice;
